// Écran de connexion : mot de passe (OPAQUE), puis second facteur si activé.

import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { unlockMasterKey } from '../crypto/master-key';
import { publishRecoveryKey } from '../crypto/recovery';
import {
  forgetDevice,
  openDeviceSession,
  rememberedDeviceId,
} from '../crypto/mls-state';
import { loadPendingRecovery } from '../crypto/recovery-flow';
import { RecoverAccount } from './RecoverAccount';
import {
  isTwoFactorChallenge,
  login,
  me as fetchMe,
  register,
  submitTwoFactor,
  type TwoFactorChallenge,
} from './opaque';
import { useAuth } from './store';
import { tenantFromHost } from './tenant';

/**
 * En production, le cabinet est lu dans l'adresse et l'utilisateur n'a rien à
 * saisir. En développement (`localhost`), il n'y a pas de sous-domaine : on le
 * demande, et l'API ne l'accepte que si elle est explicitement configurée pour
 * (`DELO_TRUST_TENANT_HEADER=1`).
 */
const TENANT_FROM_HOST = tenantFromHost();

export function LoginForm() {
  const signIn = useAuth((s) => s.signIn);

  // Le cabinet crée les comptes SANS mot de passe et remet une invitation à
  // usage unique. C'est elle — et elle seule — qui permet d'en poser un.
  const [firstSignIn, setFirstSignIn] = useState(
    () => new URLSearchParams(window.location.search).has('invitation'),
  );
  const [invitationToken, setInvitationToken] = useState(
    () => new URLSearchParams(window.location.search).get('invitation') ?? '',
  );
  // Slug du cabinet (`cabinet-p3`), pas un UUID : c'est ce que porte l'adresse.
  const [tenantSlug, setTenantSlug] = useState(TENANT_FROM_HOST ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  // Ce qu'il faut retenir pour l'étape 2FA : après un enrôlement, le cabinet et
  // l'e-mail viennent du jeton d'invitation, pas du formulaire.
  const [pending, setPending] = useState({ slug: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Une demande de récupération non close reprend la main d'elle-même : c'est
  // depuis CE navigateur — et lui seul — qu'elle peut aboutir, sa clé éphémère
  // n'existant nulle part ailleurs.
  const [recovering, setRecovering] = useState(() => loadPendingRecovery() !== null);

  /**
   * Finalise la connexion en trois temps, chacun nécessaire au suivant :
   *
   *  1. l'identité vue par le serveur ;
   *  2. la clé maîtresse K, déballée avec la clé dérivée du mot de passe (ou
   *     créée au premier enrôlement) ;
   *  3. l'identité MLS de cet appareil, déchiffrée avec une sous-clé de K —
   *     ou créée puis enrôlée s'il s'agit d'un appareil neuf.
   *
   * Sans l'étape 3, chaque connexion repartirait d'une identité MLS neuve et
   * l'appareil perdrait sa place dans tous les groupes auxquels il appartient.
   */
  async function complete(
    session: Awaited<ReturnType<typeof submitTwoFactor>>,
    slug: string,
    mail: string,
  ) {
    const identity = await fetchMe(session.sessionToken);
    const masterKey = await unlockMasterKey(session.sessionToken, session.exportKey);

    // Sans clé de récupération publiée, personne ne peut nous confier de part —
    // on ne pourrait donc pas être dépositaire. Elle est dérivée de la clé
    // maîtresse : rien de plus à conserver.
    await publishRecoveryKey(session.sessionToken, masterKey.masterKey);

    // L'appareil est mémorisé sous le SLUG du cabinet, pas son UUID : c'est le
    // seul identifiant dont on dispose avant de s'être connecté.
    const outcome = await openDeviceSession(
      session.sessionToken,
      masterKey.masterKey,
      slug,
      mail,
      identity.userId,
    );
    signIn(session, identity, masterKey, outcome, { tenantSlug: slug, email: mail });
  }

  async function onSubmitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    let tenant = tenantSlug.trim();
    let mail = email.trim();
    try {
      // Sur invitation : le jeton désigne le compte, donc le serveur nous rend
      // le cabinet et l'e-mail — l'invité n'a rien à saisir d'autre. On enchaîne
      // ensuite sur le login avec ce qu'il vient de définir.
      if (firstSignIn) {
        const enrolled = await register(invitationToken.trim(), password);
        tenant = enrolled.tenantSlug;
        mail = enrolled.email;
        setTenantSlug(tenant);
        setEmail(mail);
        setFirstSignIn(false);

        // Le jeton est consommé : on le sort de l'adresse. Sans ça, il resterait
        // dans la barre du navigateur et dans l'historique, et un simple retour à
        // l'écran de connexion (déconnexion, rechargement) rouvrirait « activer
        // mon compte » avec une invitation morte.
        window.history.replaceState(null, '', window.location.pathname);
      }

      // Le jeton de session doit porter l'identité de l'appareil DÈS son
      // émission (elle y est figée) : on présente donc le device mémorisé.
      const knownDevice = rememberedDeviceId(tenant, mail) ?? undefined;

      let outcome;
      try {
        outcome = await login(tenant, mail, password, knownDevice);
      } catch (e) {
        // L'appareil a pu être révoqué depuis (ou appartenir à un autre compte) :
        // on l'oublie et on se reconnecte sans lui, quitte à le ré-enrôler.
        if (!knownDevice || !(e instanceof ApiError) || e.status !== 401) throw e;
        forgetDevice(tenant, mail);
        outcome = await login(tenant, mail, password);
      }

      if (isTwoFactorChallenge(outcome)) {
        setChallenge(outcome);
        setPending({ slug: tenant, email: mail });
      } else {
        await complete(outcome, tenant, mail);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitTotp(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      await complete(
        await submitTwoFactor(challenge, totpCode.trim()),
        pending.slug,
        pending.email,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const field =
    'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm ' +
    'focus:border-blue-600 focus:outline-none';
  const button =
    'mt-6 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white ' +
    'hover:bg-blue-800 disabled:opacity-50';

  if (recovering) {
    return <RecoverAccount onDone={() => setRecovering(false)} />;
  }

  if (challenge) {
    return (
      <form onSubmit={onSubmitTotp} className="mx-auto mt-10 max-w-sm text-left">
        <h2 className="text-lg font-semibold text-slate-900">Second facteur</h2>
        <p className="mt-1 text-sm text-slate-500">
          Mot de passe validé. Saisis le code de ton application
          d&apos;authentification.
        </p>

        <label className="mt-6 block text-sm font-medium text-slate-700">
          Code à 6 chiffres
          <input
            className={field}
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            data-testid="totp"
            autoFocus
          />
        </label>

        {error && (
          <p data-testid="auth-error" className="mt-4 text-sm text-red-600">
            {error}
          </p>
        )}

        <button type="submit" disabled={busy} className={button} data-testid="submit-totp">
          {busy ? 'Vérification…' : 'Valider'}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmitPassword} className="mx-auto mt-10 max-w-sm text-left">
      <h2 className="text-lg font-semibold text-slate-900">
        {firstSignIn ? 'Activer mon compte' : 'Connexion'}
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        {firstSignIn
          ? 'Colle ton invitation et choisis ton mot de passe. Il ne sera transmis à personne — pas même au serveur.'
          : 'Ton mot de passe ne quitte pas cet appareil : le serveur ne le voit jamais.'}
      </p>

      {firstSignIn ? (
        // L'invitation désigne le compte : ni cabinet ni e-mail à saisir, et
        // personne ne peut viser un compte qui n'est pas le sien.
        <label className="mt-6 block text-sm font-medium text-slate-700">
          Invitation
          <textarea
            className={`${field} h-20 font-mono text-xs`}
            value={invitationToken}
            onChange={(e) => setInvitationToken(e.target.value)}
            placeholder="Le jeton remis par le cabinet"
            data-testid="invitation-token"
          />
        </label>
      ) : (
        <>
          {/* En production, le cabinet est dans l'adresse : rien à saisir. */}
          {!TENANT_FROM_HOST && (
            <label className="mt-6 block text-sm font-medium text-slate-700">
              Cabinet
              <input
                className={field}
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                placeholder="ex. cabinet-dupont"
                data-testid="tenant"
              />
            </label>
          )}

          <label className="mt-4 block text-sm font-medium text-slate-700">
            Adresse e-mail
            <input
              className={field}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              data-testid="email"
            />
          </label>
        </>
      )}

      <label className="mt-4 block text-sm font-medium text-slate-700">
        Mot de passe
        <input
          className={field}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          data-testid="password"
        />
      </label>

      {error && (
        <p data-testid="auth-error" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <button type="submit" disabled={busy} className={button} data-testid="submit-login">
        {busy ? 'Connexion…' : firstSignIn ? 'Définir et se connecter' : 'Se connecter'}
      </button>

      <button
        type="button"
        onClick={() => {
          setFirstSignIn((v) => !v);
          setError(null);
        }}
        data-testid="toggle-first-signin"
        className="mt-4 w-full text-xs text-slate-500 underline hover:text-slate-700"
      >
        {firstSignIn
          ? "J'ai déjà un mot de passe"
          : "J'ai une invitation — activer mon compte"}
      </button>

      {!firstSignIn && (
        <button
          type="button"
          onClick={() => setRecovering(true)}
          data-testid="forgot-password"
          className="mt-2 w-full text-xs text-slate-500 underline hover:text-slate-700"
        >
          Mot de passe oublié
        </button>
      )}
    </form>
  );
}
